// The one sign-in session, from kast-common's auth: tokens from
// login.kast-dev.net under the family's auth_* keys, refreshed before
// expiry, on every page load and on a 401 or 403, revoked on sign-out, and
// kept in step with the app's other tabs. What stays
// here is CloudBoy's own: its Cognito app client, its admin groups, the
// desktop app's sign-in, and the page loads that keep saves with their
// owner (emulator/profiles.ts).
//
// Sign-in is optional: nothing here gates a page, and signed out the app
// plays anonymously.

import {
  createSession,
  hostedUiLogoutUrl,
  localStorageTokenStore,
  redirectToSignIn,
  takeTokensFromUrl,
  type SessionEnd,
} from "@jkast90/kast-common/auth";
import { getDeploymentConfig } from "@common/deployment-config";
import { LOGIN_URL, POST_LOGIN_PATH_KEY } from "@common/constants";
import { DESKTOP_SIGN_IN, isDesktop } from "../desktop/desktop";

/**
 * Groups that open CloudBoy's admin page: its own, and the shared kast-dev
 * admins. Matches AdminUser in api/src/auth.rs and the Cognito groups'
 * names exactly, as `hasGroup` compares them (the API also lets other
 * casings through). This only decides what to show: the API checks.
 */
export const ADMIN_GROUPS = ["pocket-play-admin", "admin"] as const;

const { cognitoDomain, clientId } = getDeploymentConfig();

export const session = createSession({
  cognitoDomain,
  clientId,
  // userEmail is the old slice's leftover; cleared on sign-out.
  store: localStorageTokenStore({ alsoClear: ["userEmail"] }),
  // Tokens carry the groups as of their issue (ID tokens last 10 hours), so
  // a newly granted group (CloudBoy access, admin) shows on the next load.
  refreshOnStart: true,
});

const isPlayer = (path: string) => path === "/play" || path.startsWith("/play?");

/**
 * Back from login.kast-dev.net: store the hash's tokens and scrub them from
 * the URL, before settleProfile reads who is signed in. When sign-in came
 * back somewhere other than where it started, go there: by a full page load
 * for the player, the one cross-origin-isolated page (AGENTS.md), which is
 * when this returns true and the page shouldn't start.
 */
export function takeSignInFromUrl(): boolean {
  const returnTo = takeTokensFromUrl(session, { returnKey: POST_LOGIN_PATH_KEY })?.returnTo;
  if (!returnTo) return false;
  if (isPlayer(returnTo)) {
    window.location.replace(returnTo);
    return true;
  }
  window.history.replaceState(null, "", returnTo);
  return false;
}

/**
 * To login.kast-dev.net and back to this page. The desktop app signs in
 * through the system browser instead (DesktopSignIn), so its window stays.
 */
export function signIn(): void {
  if (isDesktop) {
    window.dispatchEvent(new Event(DESKTOP_SIGN_IN));
    return;
  }
  redirectToSignIn({ signInUrl: `${LOGIN_URL}/signin`, returnKey: POST_LOGIN_PATH_KEY });
}

/**
 * Revokes the refresh token, clears the session, then leaves: through the
 * hosted UI's logout when configured, else a reload. Either way the page
 * loads again signed out, so settleProfile puts the account's saves aside
 * before anyone plays on.
 */
export function signOut(): Promise<void> {
  return session.signOut({
    leave: () =>
      cognitoDomain && clientId
        ? window.location.assign(hostedUiLogoutUrl({ cognitoDomain, clientId, logoutUri: window.location.origin }))
        : window.location.reload(),
  });
}

// Every way a session ends without this page's own sign-out (which leaves
// by itself): Cognito refused the refresh, it expired with nothing to
// refresh it, or another tab signed out (its tokens left storage).
const ENDED_UNDER_THE_PAGE: readonly SessionEnd[] = ["rejected", "expired", "otherTab"];

/**
 * A session that ends under the page reloads it, as a sign-out does, so
 * settleProfile puts the account's saves aside before anyone plays on
 * signed out: without the reload, signed-out play here would go into that
 * account's saves. Arm it once the page has read who is signed in. A
 * transient refresh failure keeps the session, so it never gets here.
 */
export function reloadWhenSessionEnds(): () => void {
  return session.subscribe(() => {
    const { status, endedBy } = session.getState();
    if (status === "signedOut" && endedBy && ENDED_UNDER_THE_PAGE.includes(endedBy)) window.location.reload();
  });
}
